import InfiniteScroll from 'react-infinite-scroll-component';
import classNames from 'classnames';
import { CSSProperties, ReactNode, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks';
import { ArrowLongDownIcon, ArrowShortLeftIcon, ArrowShortRightIcon } from '../icons';
import { Select, SelectOption } from './Select';
import { Skeleton } from './Skeleton';
import { Spinner } from './Spinner';
import styles from './Table.module.css';

export enum CustomFieldType {
  Text = 'text',
  Checkbox = 'checkbox',
  Number = 'number',
}

export type TableHeadColumnSorting = 'asc' | 'desc';

export interface TableHeadColumn<T = string | ReactNode> {
  title: T;
  className?: string;
  style?: CSSProperties;
  width?: number;
  onChangeSorting?: () => void;
  sorting?: TableHeadColumnSorting;
  show?: boolean;
  position?: number;
  custom?: boolean;
  customType?: CustomFieldType;
}

export interface BodyCell {
  cell: string | ReactNode | JSX.Element;
  className?: string;
  position?: number;
  custom?: boolean;
}

export interface TableBodyColumn {
  cells: BodyCell[];
  onClick?: () => void;
}

export interface TableProps {
  isLoading?: boolean;
  isError?: boolean;
  isSearchUsed?: boolean;
  headItems?: TableHeadColumn[];
  items: TableBodyColumn[];
  total?: number;
  totalPage?: number;
  page?: number;
  setPage?: React.Dispatch<number>;
  limit?: number;
  setLimit?: React.Dispatch<number>;
  withBorder?: boolean;
  withBorderX?: boolean;
  withRounded?: boolean;
  hasNextPageInfinite?: boolean;
  fetchNextPageInfinite?: () => void;
  blockIdInfinite?: string;
  queryPage?: string;
  queryLimit?: string;
  className?: string;
  withQueryParams?: boolean;
  classNameHead?: string;
  mobileFirstCellShowInHeadLeft?: boolean;
  mobileLastCellShowInHeadRight?: boolean;
}

export const Table = ({
  headItems,
  items,
  total,
  totalPage,
  isLoading,
  page,
  setPage,
  limit,
  setLimit,
  withBorder = true,
  withRounded = true,
  hasNextPageInfinite,
  fetchNextPageInfinite,
  blockIdInfinite,
  isError,
  isSearchUsed = false,
  queryPage = 'page',
  queryLimit = 'limit',
  className,
  withBorderX = true,
  withQueryParams = true,
  classNameHead,
  mobileLastCellShowInHeadRight = false,
  mobileFirstCellShowInHeadLeft = false,
}: TableProps) => {
  const { pathname, search: query } = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const queryParams = useMemo(() => new URLSearchParams(query), [query]);
  const headFixItems = useMemo<TableHeadColumn[] | undefined>(
    () =>
      headItems && [
        { title: '', position: -1 },
        ...headItems.map(head => ({ ...head, position: head.position ?? 1000 })),
        { title: '', position: 10001 },
      ],
    [headItems],
  );

  const bodyFixItems = useMemo<TableBodyColumn[]>(() => {
    const countCustomField = headItems?.filter(head => head.custom === true)?.length || 0;
    return items.map(item => {
      const newCells: BodyCell[] =
        !headItems || headItems.length === item.cells.length
          ? item.cells
          : headItems.map((head, i): BodyCell => {
              if (head?.custom === true && !item.cells?.[i]?.custom) {
                return {
                  cell: '',
                  custom: true,
                };
              }
              if (head.title !== '') {
                return item.cells?.[i];
              }
              return item.cells?.[i - countCustomField];
            });

      const updItem: TableBodyColumn =
        !headItems || headItems.length === item.cells.length
          ? item
          : ({
              ...item,
              cells: newCells,
            } as TableBodyColumn);

      return {
        ...item,
        cells: [
          { cell: '', position: -1 },
          ...updItem.cells.map((body, i) => ({
            ...body,
            position: headItems?.[i].position ?? 1000,
          })),
          { cell: '', position: 10001 },
        ],
      };
    });
  }, [items, headItems]);

  const handleChangeLimit = useCallback(
    (value: number | null) => {
      if (value && setLimit) {
        setLimit(value);
        if (withQueryParams) {
          queryParams.set(queryLimit, String(value));
          const link = `${pathname}?${queryParams.toString()}`;
          navigate(link);
        }
      }
    },
    [navigate, pathname, queryLimit, queryParams, setLimit, withQueryParams],
  );

  const handleChangePageQuery = useCallback(
    (p: number) => {
      if (withQueryParams) {
        queryParams.set(queryPage, String(p));
        const link = `${pathname}?${queryParams.toString()}`;
        navigate(link);
      }
    },
    [navigate, pathname, queryPage, queryParams, withQueryParams],
  );

  const handleChangePage = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const p = +e.target.value;
      if (totalPage && p > totalPage) {
        setPage && setPage(totalPage);
        handleChangePageQuery(totalPage);
        return;
      }
      if (p < 1) {
        setPage && setPage(1);
        handleChangePageQuery(1);
        return;
      }
      handleChangePageQuery(p);
      setPage && setPage(p);
    },
    [handleChangePageQuery, setPage, totalPage],
  );

  const handlePrevPage = useCallback(() => {
    if (totalPage === 0) {
      return;
    }
    if (!page || page <= 1) {
      handleChangePageQuery(1);
      setPage && setPage(1);
      return;
    }
    handleChangePageQuery(page - 1);
    setPage && setPage(page - 1);
  }, [handleChangePageQuery, page, setPage, totalPage]);

  const handleNextPage = useCallback(() => {
    if (totalPage === 0) {
      return;
    }
    if (totalPage && (page || 0) >= totalPage) {
      handleChangePageQuery(totalPage);
      setPage && setPage(totalPage);
      return;
    }
    handleChangePageQuery((page || 0) + 1);
    setPage && setPage((page || 0) + 1);
  }, [handleChangePageQuery, page, setPage, totalPage]);

  const currentItems = items.length || 0;

  const selectCountItemsPages = useMemo<SelectOption<number>[]>(() => {
    const pages: SelectOption<number>[] = [];

    if (total && limit) {
      const max = Math.min(...[limit > total ? limit : total, 50]);
      for (let i = 1; i <= max; i++) {
        pages.push({
          label: i,
          value: i,
        });
      }
    }
    return pages;
  }, [total, limit]);

  const defaultItemLimitPage = useMemo<number | undefined>(() => {
    const l = limit || 8;
    const t = total || 1;
    return limit !== undefined || l > t || t > 50 ? l : t;
  }, [limit, total]);

  const table = (
    <table className="w-full">
      {headFixItems && (
        <thead>
          <tr
            className={classNames(
              'border-b border-gray-200 select-none',
              {
                'border-t': withBorder === true && !classNameHead?.includes('border-'),
                'bg-gray-100': !classNameHead?.includes('bg-'),
              },
              classNameHead,
            )}
          >
            {headFixItems
              .filter(head => head.show !== false)
              .sort((a, b) => (a.position ?? 1000) - (b.position ?? 1000))
              .map((head, i, array) => (
                <th
                  key={'head_' + i + (typeof head.title === 'string' ? head.title : 'ReactNode')}
                  style={{ ...head.style, minWidth: head.width ?? head.style?.minWidth }}
                  className={classNames(
                    'py-3',
                    {
                      'min-w-[24px] w-[24px]': i === 0 || i === array.length - 1,
                      'rounded-tl-lg': withRounded === true && i === 0,
                      'rounded-tr-lg': withRounded === true && i === array.length - 1,
                      'text-left text-sm leading-[22px]': i > 0 && i < array.length - 1,
                      'border-r border-r-gray-200':
                        ((i === 1 && i < array.length - 2) ||
                          (i > 1 && array.length > 2 && i < array.length - 2)) &&
                        withBorderX,
                      'pr-6': i === 1 && i < array.length - 2,
                      'pl-6': array.length > 2 && i === array.length - 2,
                      'px-6': i > 1 && array.length > 2 && i < array.length - 2,
                      'font-normal': !head?.className?.includes('font-'),
                      'text-gray-600': !head?.className?.includes('text-'),
                    },
                    head.className,
                  )}
                >
                  <span
                    className={classNames({
                      'inline-block align-middle': head.onChangeSorting && head.sorting,
                    })}
                  >
                    {head.title}
                  </span>
                  {head.onChangeSorting && head.sorting && (
                    <span className="inline-block align-middle pl-2">
                      <img
                        alt="sorting"
                        src={ArrowLongDownIcon}
                        className={classNames(
                          'w-4 min-w-[16px] hover:opacity-80 hover:shadow-sm hover:cursor-pointer',
                          {
                            'rotate-180': head.sorting === 'desc',
                          },
                        )}
                        onClick={head.onChangeSorting}
                      />
                    </span>
                  )}
                </th>
              ))}
          </tr>
        </thead>
      )}
      <tbody>
        {isLoading === true || items.length === 0 ? (
          <EmptyStateTable
            isError={isError}
            isLoading={isLoading}
            headItems={headFixItems}
            isSearchUsed={isSearchUsed}
          />
        ) : (
          bodyFixItems.map((item, j, arrayCol) => (
            <tr
              key={'body_tr_' + j}
              onClick={item.onClick}
              className={classNames({
                'hover:cursor-pointer hover:bg-gray-50': !!item.onClick,
              })}
            >
              {item.cells
                .filter((_, i) => !headFixItems || headFixItems?.[i].show !== false)
                .sort((a, b) => (a.position ?? 1000) - (b.position ?? 1000))
                .map((itemCell, i, arrayRow) => (
                  <td
                    key={'body_tr_' + j + '_td_' + i}
                    onClick={
                      headFixItems?.filter(head => head?.show !== false)?.[i].title === '' && i > 0
                        ? e => {
                            e.stopPropagation();
                          }
                        : undefined
                    }
                    className={classNames(
                      'py-3',
                      {
                        'border-b border-b-gray-200': j < arrayCol.length - 1,
                        'min-w-[24px] w-[24px]': i === 0 || i === arrayRow.length - 1,
                        'text-left text-sm leading-[22px]': i > 0 && i < arrayRow.length - 1,
                        'border-r border-r-gray-200':
                          ((i === 1 && i < arrayRow.length - 2) ||
                            (i > 1 && arrayRow.length > 2 && i < arrayRow.length - 2)) &&
                          withBorderX,
                        'pr-6': i === 1 && i < arrayRow.length - 2,
                        'pl-6': arrayRow.length > 2 && i === arrayRow.length - 2,
                        'px-6': i > 1 && arrayRow.length > 2 && i < arrayRow.length - 2,
                        'text-gray-900': !itemCell.className?.includes('text-'),
                      },
                      itemCell.className,
                    )}
                  >
                    {itemCell.cell}
                  </td>
                ))}
            </tr>
          ))
        )}
      </tbody>
      {page !== undefined && limit !== undefined && (
        <tfoot>
          <tr className="border-t border-t-gray-200 select-none">
            <td className="py-4 mim-w-[24px] w-[24px]"></td>
            <td
              colSpan={
                (headFixItems
                  ? headFixItems.filter(head => head.show !== false).length
                  : bodyFixItems?.[0]?.cells?.length || 3) - 2
              }
              className="py-4"
            >
              <div className="flex flex-row items-center justify-between text-sm leading-[22px]">
                <div className="flex flex-row items-center justify-center">
                  <span className="pr-4 text-gray-900">Items per page</span>
                  <span className="min-w-[96px] w-[96px] h-[38px]">
                    <Select
                      defaultValue={defaultItemLimitPage}
                      options={selectCountItemsPages}
                      onChange={handleChangeLimit}
                      position="absolute"
                      positionMenu="up"
                      disabled={!setLimit}
                    />
                  </span>
                  <span className="text-gray-600">
                    {(() => {
                      const current = (page || 1) * limit - limit;
                      return (
                        <>
                          {current || 1}-{currentItems + current} of {total} items
                        </>
                      );
                    })()}
                  </span>
                </div>
                <div className="flex flex-row items-center justify-center">
                  <span className="mr-10">
                    {isLoading && items.length > 0 && <Spinner size="sm" />}
                  </span>
                  <span
                    className={classNames(
                      'flex select-none flex-row items-center justify-center pr-4 hover:cursor-pointer hover:text-[#818CF8]',
                      {
                        'text-gray-500 hover:text-gray-500 hover:cursor-default':
                          page === 1 || totalPage === 0,
                        'text-blue-500': (totalPage || 0) > 0 && page > 1,
                      },
                    )}
                    onClick={handlePrevPage}
                  >
                    <span>
                      <img src={ArrowShortLeftIcon} alt="Prev Page" className="w-2.5 h-2.5" />
                    </span>
                    <span className="pl-2">Previous</span>
                  </span>
                  <span className="pl-1">
                    <input
                      className="w-9 rounded border border-solid text-center p-1.5 focus-visible:outline-[#6366F1]"
                      value={page || ''}
                      onChange={handleChangePage}
                    />
                  </span>
                  <span className="pr-8 pl-2">of {totalPage}</span>
                  <span
                    className={classNames(
                      'flex select-none flex-row items-center justify-center hover:cursor-pointer hover:text-[#818CF8]',
                      {
                        'text-gray-500 hover:text-gray-500 hover:cursor-default':
                          page === totalPage || totalPage === 0,
                        'text-blue-500': (totalPage || 0) > 0 && page < (totalPage || 0),
                      },
                    )}
                    onClick={handleNextPage}
                  >
                    <span className="pr-2">Next</span>
                    <span>
                      <img src={ArrowShortRightIcon} alt="Next Page" className="w-2.5 h-2.5" />
                    </span>
                  </span>
                </div>
              </div>
            </td>
            <td className="py-4 min-w-[24px] w-[24px]"></td>
          </tr>
        </tfoot>
      )}
    </table>
  );

  const card =
    (isLoading === true && !hasNextPageInfinite && !fetchNextPageInfinite) || items.length === 0 ? (
      <MobileEmptyStateTable
        isLoading={isLoading}
        isError={isError}
        isSearchUsed={isSearchUsed}
        withBorderX={withBorderX}
        withBorder={withBorder}
        withRounded={withRounded}
      />
    ) : (
      <div
        className={classNames('flex flex-col w-full px-2 py-4', {
          'gap-4': withBorder === true,
        })}
      >
        {bodyFixItems.map((item, col, allCol) => (
          <div
            className={classNames('w-full flex flex-col gap-2 py-2', {
              border: withBorder === true,
              'rounded-lg': withRounded === true,
              'border-b': !withBorder && withBorderX === true && col !== allCol.length - 1,
              'hover:cursor-pointer hover:bg-gray-50': !!item.onClick,
            })}
            key={`col_${col}`}
            onClick={item.onClick}
          >
            {item.cells.map((cell, row, allRow) => {
              if (
                row === 0 ||
                row === allRow.length - 1 ||
                (mobileLastCellShowInHeadRight === true && row === allRow.length - 2)
              ) {
                return null;
              }
              const head = headFixItems?.[row];
              const lastCell =
                row === 1 &&
                mobileLastCellShowInHeadRight === true &&
                mobileFirstCellShowInHeadLeft === true
                  ? item.cells?.[allRow.length - 2]
                  : undefined;

              return (
                <div
                  className="flex gap-2 items-start justify-between text-xs leading-5 px-4"
                  key={`row_${row}`}
                >
                  {head?.title && (mobileFirstCellShowInHeadLeft === false || row !== 1) && (
                    <div className="text-gray-500 max-w-[calc(50%-8px)] overflow-x-hidden text-ellipsis break-all">
                      {head?.title}
                    </div>
                  )}
                  <div
                    className={classNames('text-gray-900 relative', {
                      'w-[50%]':
                        !!head?.title && (mobileFirstCellShowInHeadLeft === false || row !== 1),
                      'w-full':
                        !head?.title && (mobileFirstCellShowInHeadLeft === false || row !== 1),
                      'max-w-[calc(50%-8px)]':
                        mobileFirstCellShowInHeadLeft === true &&
                        mobileLastCellShowInHeadRight === true &&
                        row === 1,
                    })}
                  >
                    {cell.cell}
                  </div>
                  {mobileLastCellShowInHeadRight === true && row === 1 && lastCell && (
                    <div
                      className={classNames('text-gray-900 relative', {
                        'w-[50%]': mobileFirstCellShowInHeadLeft === true,
                        'w-full': mobileFirstCellShowInHeadLeft === false,
                      })}
                    >
                      {lastCell.cell}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );

  return (
    <div
      className={classNames(
        'w-full bg-white flex items-center justify-center',
        {
          border: !isMobile && withBorder === true && !className?.includes('border-'),
          'rounded-lg': !isMobile && withRounded === true,
          'my-1': !className?.includes('my-'),
        },
        className,
      )}
    >
      <div className={classNames('overflow-x-auto w-full relative', styles.scroll)}>
        {hasNextPageInfinite && fetchNextPageInfinite ? (
          <InfiniteScroll
            scrollableTarget={blockIdInfinite}
            next={fetchNextPageInfinite}
            dataLength={currentItems ?? 0}
            hasMore={hasNextPageInfinite}
            loader={
              <div className="flex justify-center items-center h-16 w-full">
                <Spinner size="md" variant="primary" />
              </div>
            }
            style={{ overflow: 'hidden!important' }}
          >
            {isMobile ? card : table}
          </InfiniteScroll>
        ) : isMobile ? (
          card
        ) : (
          table
        )}
      </div>
    </div>
  );
};

export interface EmptyStateTableProps {
  headItems?: TableHeadColumn[];
  isError?: boolean;
  isSearchUsed?: boolean;
  isLoading?: boolean;
  withBorder?: boolean;
  withRounded?: boolean;
  withBorderX?: boolean;
}

export const EmptyStateTable = ({
  headItems,
  isError,
  isLoading,
  isSearchUsed,
}: EmptyStateTableProps) => {
  return (
    <>
      {headItems &&
        new Array(6).fill(0).map((_, col, all) => (
          <tr
            key={'empty_tr_' + col}
            className={classNames({
              'border-b-gray-200 border-b': col < all.length - 1,
            })}
            style={{ opacity: 1.2 - col * 0.2, position: col === 4 ? 'relative' : undefined }}
          >
            {headItems
              .filter(head => head.show !== false)
              .sort((a, b) => (a.position ?? 1000) - (b.position ?? 1000))
              .map((head, i, array) => (
                <td
                  key={
                    'empty_tr_' +
                    col +
                    '_td_' +
                    i +
                    (typeof head.title === 'string' ? head.title : '_')
                  }
                  className={classNames({
                    'w-[24px]': i === 0 || i === array.length - 1,
                    'text-left text-xs leading-5 text-gray-600': i > 0 && i < array.length - 1,
                    'pr-6': i === 1,
                    'pl-6': array.length > 2 && i === array.length - 2,
                    'px-6': i > 1 && array.length > 2 && i < array.length - 2,
                    'py-3': !head.className?.includes('py-'),
                  })}
                >
                  {i > 0 &&
                    i < array.length - 1 && ( //&& head.title !== ''
                      <span
                        className={classNames('flex', {
                          'justify-end': head.className?.includes('right'),
                          'justify-center': head.className?.includes('center'),
                        })}
                      >
                        {isLoading ? (
                          <span className="flex flex-col w-full">
                            <Skeleton width={`${Math.floor(Math.random() * 61) + 40}px`} />
                          </span>
                        ) : (
                          <span className="flex flex-col w-full">
                            <span
                              style={{ width: Math.floor(Math.random() * 61) + 40 }}
                              className="flex flex-col border-[7px] rounded-lg"
                            ></span>
                          </span>
                        )}
                      </span>
                    )}
                </td>
              ))}
            {col === 4 && (
              <td className="absolute opacity-100 flex items-center justify-center m-auto top-[-36px] left-0 right-0 bottom-auto text-[16px] leading-6 font-bold text-[#475569]">
                {isSearchUsed && !isError && !isLoading && 'No items found'}
                {!isSearchUsed && !isError && !isLoading && 'No data yet'}
                {isError && 'Error loading:('}
              </td>
            )}
          </tr>
        ))}
    </>
  );
};

export const MobileEmptyStateTable = ({
  isError,
  isLoading,
  isSearchUsed,
  withBorder,
  withRounded,
  withBorderX,
}: EmptyStateTableProps) => {
  return (
    <>
      <div
        className={classNames('flex flex-col w-full px-2 py-4', {
          'gap-4': withBorder === true,
        })}
      >
        {new Array(isLoading ? 2 : 1).fill(0).map((_, col, allCol) => (
          <div
            className={classNames('w-full flex flex-col gap-2 py-3', {
              border: withBorder === true,
              'rounded-lg': withRounded === true,
              'border-b': !withBorder && withBorderX === true && col !== allCol.length - 1,
            })}
            key={`col_${col}`}
          >
            {new Array(6).fill(0).map((_, row) => {
              return (
                <div
                  className={classNames(
                    'flex gap-2 items-start justify-between text-xs leading-5 px-4',
                    { relative: row === 4 },
                  )}
                  key={`row_${row}`}
                >
                  <div className="text-gray-500 max-w-[calc(50%-8px)] overflow-x-hidden text-ellipsis break-all">
                    {isLoading ? (
                      <Skeleton width={`${Math.floor(Math.random() * 61) + 40}px`} height="12px" />
                    ) : (
                      <span
                        style={{ width: Math.floor(Math.random() * 61) + 40 }}
                        className="flex flex-col border-[7px] rounded-lg"
                      ></span>
                    )}
                  </div>
                  <div className={'text-gray-900 relative w-[50%]'}>
                    {isLoading ? (
                      <Skeleton width={`${Math.floor(Math.random() * 61) + 40}px`} height="12px" />
                    ) : (
                      <span
                        style={{ width: Math.floor(Math.random() * 61) + 40 }}
                        className="flex flex-col border-[7px] rounded-lg"
                      ></span>
                    )}
                  </div>
                  {row === 4 && (
                    <div className="absolute opacity-60 flex items-center justify-center m-auto top-[-40px] left-0 right-0 bottom-auto text-sm leading-[22px] font-bold text-[#475569]">
                      {isSearchUsed && !isError && !isLoading && 'No items found'}
                      {!isSearchUsed && !isError && !isLoading && 'No data yet'}
                      {isError && 'Error loading:('}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
};
